import {
  ActivityIcon,
  ArrowUpRightIcon,
  BeakerIcon,
  BriefcaseIcon,
  ChevronDownIcon,
  ClockIcon,
  DollarSignIcon,
  FileIcon,
  FileTextIcon,
  FilterXIcon,
  GaugeIcon,
  LinkIcon,
  ListChecksIcon,
  UsersIcon,
} from "lucide-react";
import { useRef } from "react";

import { JobBottleneckBadge } from "@/components/job-tracker/badges/JobBottleneckBadge";
import { JobHealthBadge } from "@/components/job-tracker/badges/JobHealthBadge";
import { TableJobDifficultyBadge } from "./badges/TableJobDifficultyBadge";
import { TableJobUrgencyBadge } from "./badges/TableJobUrgencyBadge";
import {
  Column,
  EmptyDataDash,
  EmptyState,
  Row,
  TableRoleCell,
  TableTooltipParagraph,
  TimeAgoInTableCell,
  TrackerTableHeaderContainer,
} from "@/components";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

import { useTaggableUsers } from "@/contexts/TaggableUsersContext";
import {
  requisitionStatusOptions,
  requisitionStatusToBackgroundColor,
  requisitionStatusToHoverBackgroundColor,
  requisitionStatusToLabel,
} from "@/lib/constants";
import { mixpanelUtils } from "@/lib/mixpanel/mixpanel";
import { MIXPANEL_SCREENS } from "@/lib/mixpanel/types";
import { cn } from "@/lib/utils";
import {
  JobQueryParams,
  JobTrackerGroupBy,
  JobTrackerResponse,
  RequisitionStatus,
} from "@/types";

export const JobTrackerTable = ({
  groupBy,
  handleUpdateView,
  jobQueryParams,
  jobs,
  selectedIndex,
  setIsJobUpdateModalOpen,
  setJobsForUpdateModal,
  setSelectedIndex,
}: {
  groupBy?: JobTrackerGroupBy;
  handleUpdateView: (jobQueryParams: JobQueryParams) => void;
  jobQueryParams: JobQueryParams;
  jobs: JobTrackerResponse[];
  selectedIndex: number | null;
  setIsJobUpdateModalOpen: (isOpen: boolean) => void;
  setJobsForUpdateModal: (jobs: JobTrackerResponse[]) => void;
  setSelectedIndex: (index: number | null) => void;
}) => {
  const { userOptions } = useTaggableUsers();

  let groupByOptions: {
    label: string;
    value: number | string | RequisitionStatus;
  }[] = [];
  if (groupBy === JobTrackerGroupBy.RequisitionOwner) {
    groupByOptions = [
      ...userOptions,
      {
        label: "Unassigned",
        value: "unassigned",
      },
    ];
  } else if (groupBy === JobTrackerGroupBy.RequisitionStatus) {
    groupByOptions = requisitionStatusOptions;
  }

  const render = () => {
    if (groupBy) {
      return (
        <>
          {groupByOptions.map((groupByOption) => {
            const groupedJobs = jobs.filter(
              (job) => job.groupByKey === groupByOption.value,
            );
            if (groupedJobs.length === 0) return null;
            return (
              <Column
                key={groupByOption.value}
                className="mt-4 overflow-hidden rounded-lg border shadow-sm first:mt-0"
              >
                <Collapsible className="group/collapsible" defaultOpen>
                  <CollapsibleTrigger asChild>
                    <Button
                      className="flex w-full justify-between rounded-b-none bg-primary-300 p-3 hover:bg-primary-400"
                      onClick={() => {
                        mixpanelUtils.trackButtonClick(
                          "Collapsible group header toggle",
                          MIXPANEL_SCREENS.JOB_TRACKER,
                          { group: groupByOption.label },
                        );
                      }}
                    >
                      <Row className="items-center gap-2 text-slate-700">
                        <ChevronDownIcon className="transition-transform group-data-[state=open]/collapsible:rotate-180" />
                        <span>{groupByOption.label}</span>
                      </Row>
                      <Row className="items-center gap-2">
                        <Badge variant="secondary">
                          {groupedJobs.length}{" "}
                          {groupedJobs.length === 1 ? "job" : "jobs"}
                        </Badge>
                        <Badge variant="secondary">
                          $
                          {groupedJobs
                            .reduce((sum, j) => sum + j.outstandingRevenue, 0)
                            .toLocaleString(undefined, {
                              maximumFractionDigits: 2,
                            })}{" "}
                          revenue
                        </Badge>
                      </Row>
                    </Button>
                  </CollapsibleTrigger>
                  <CollapsibleContent>
                    <BaseJobTrackerTable
                      key={groupByOption.value}
                      handleClearAllFilters={handleUpdateView}
                      jobQueryParams={jobQueryParams}
                      jobs={groupedJobs}
                      groupBy={groupBy}
                      selectedIndex={selectedIndex}
                      setIsJobUpdateModalOpen={setIsJobUpdateModalOpen}
                      setJobsForUpdateModal={setJobsForUpdateModal}
                      setSelectedIndex={setSelectedIndex}
                    />
                  </CollapsibleContent>
                </Collapsible>
              </Column>
            );
          })}
        </>
      );
    }
    return (
      <BaseJobTrackerTable
        handleClearAllFilters={handleUpdateView}
        jobQueryParams={jobQueryParams}
        jobs={jobs}
        selectedIndex={selectedIndex}
        setIsJobUpdateModalOpen={setIsJobUpdateModalOpen}
        setJobsForUpdateModal={setJobsForUpdateModal}
        setSelectedIndex={setSelectedIndex}
      />
    );
  };

  return render();
};

const BaseJobTrackerTable = ({
  groupBy,
  handleClearAllFilters,
  jobQueryParams,
  jobs,
  selectedIndex,
  setIsJobUpdateModalOpen,
  setJobsForUpdateModal,
  setSelectedIndex,
}: {
  groupBy?: JobTrackerGroupBy;
  handleClearAllFilters: (jobQueryParams: JobQueryParams) => void;
  jobQueryParams: JobQueryParams;
  jobs: JobTrackerResponse[];
  selectedIndex: number | null;
  setIsJobUpdateModalOpen: (isOpen: boolean) => void;
  setJobsForUpdateModal: (jobs: JobTrackerResponse[]) => void;
  setSelectedIndex: (index: number | null) => void;
}) => {
  const { userNameMap } = useTaggableUsers();

  const render = () => {
    return (
      <div
        // If groupBy is set, we limit max height so multiple groups fit. Otherwise, use h-full to fill remaining space dynamically.
        className={cn(
          "overflow-auto",
          groupBy ? "max-h-[60vh]" : "rounded-lg border",
        )}
      >
        <table className="w-full table-fixed text-sm">
          <TableHeader className="sticky top-0 bg-slate-50 [&_tr]:border-b-0">
            <TableRow className="sticky-border-bottom">
              <TableHead className="w-[11%]">
                <TrackerTableHeaderContainer
                  Icon={BriefcaseIcon}
                  label="Role"
                />
              </TableHead>
              <TableHead className="w-[13%]">
                <TrackerTableHeaderContainer
                  Icon={GaugeIcon}
                  label="Difficulty / Urgency"
                />
              </TableHead>
              {groupBy !== JobTrackerGroupBy.RequisitionStatus && (
                <TableHead className="w-[10%]">
                  <TrackerTableHeaderContainer
                    Icon={ListChecksIcon}
                    label="Requisitions"
                  />
                </TableHead>
              )}
              {groupBy !== JobTrackerGroupBy.RequisitionOwner && (
                <TableHead className="w-[12%]">
                  <TrackerTableHeaderContainer Icon={UsersIcon} label="Owner" />
                </TableHead>
              )}
              <TableHead
                className={cn(
                  groupBy === JobTrackerGroupBy.RequisitionOwner
                    ? "w-[24%]"
                    : groupBy === JobTrackerGroupBy.RequisitionStatus
                      ? "w-[22%]"
                      : "w-[12%]",
                )}
              >
                <TrackerTableHeaderContainer
                  Icon={FileTextIcon}
                  label="Description"
                />
              </TableHead>
              <TableHead className="w-[9%]">
                <TrackerTableHeaderContainer
                  Icon={BeakerIcon}
                  label="Bottleneck"
                />
              </TableHead>
              <TableHead className="w-[9%]">
                <TrackerTableHeaderContainer
                  Icon={ActivityIcon}
                  label="Health"
                />
              </TableHead>
              <TableHead className="w-[10%]">
                <TrackerTableHeaderContainer
                  Icon={ClockIcon}
                  label="Last Update"
                />
              </TableHead>
              <TableHead className="w-[7%]">
                <TrackerTableHeaderContainer
                  Icon={DollarSignIcon}
                  label="Revenue"
                />
              </TableHead>
              <TableHead className="w-[7%]">
                <TrackerTableHeaderContainer Icon={LinkIcon} label="Link" />
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {jobs.length > 0 ? (
              jobs.map((job, index) => {
                const latestJobUpdate =
                  job.jobUpdates.length > 0 ? job.jobUpdates[0] : null;

                return (
                  <TableRow
                    key={job.jobId}
                    className="cursor-default"
                    onClick={() => {
                      mixpanelUtils.trackRowClick(
                        "Job tracker row (select)",
                        MIXPANEL_SCREENS.JOB_TRACKER,
                      );
                      setJobsForUpdateModal(jobs);
                      setSelectedIndex(index === selectedIndex ? null : index);
                    }}
                    onDoubleClick={() => {
                      mixpanelUtils.trackRowClick(
                        "Job tracker row (open)",
                        MIXPANEL_SCREENS.JOB_TRACKER,
                      );
                      setJobsForUpdateModal(jobs);
                      setSelectedIndex(index);
                      setIsJobUpdateModalOpen(true);
                    }}
                  >
                    <TableRoleCell
                      companyName={job.company.name}
                      jobPausedAt={job.jobPausedAt}
                      jobPauseReason={job.jobPauseReason}
                      jobPauseType={job.jobPauseType}
                      jobStatus={job.jobStatus}
                      jobTitle={job.jobTitle}
                      unseenExceptionalCandidaciesCount={
                        job.unseenExceptionalCandidaciesCount
                      }
                    />
                    <TableCell>
                      {(() => {
                        // jobUpdates is history, newest first — [0] is current.
                        const latest = job.jobUpdates[0];
                        if (
                          job.jobUpdates.length === 0 ||
                          (latest.difficulty == null && latest.urgency == null)
                        ) {
                          return <EmptyDataDash />;
                        }
                        return (
                          <Column className="items-start gap-1">
                            {latest.difficulty != null && (
                              <TableJobDifficultyBadge
                                difficulty={latest.difficulty}
                              />
                            )}
                            {latest.urgency != null && (
                              <TableJobUrgencyBadge urgency={latest.urgency} />
                            )}
                          </Column>
                        );
                      })()}
                    </TableCell>
                    {groupBy !== JobTrackerGroupBy.RequisitionStatus && (
                      <TooltipBadgesCell
                        keyToCountRecord={job.countsByRequisitionStatus}
                        keyToLabelRecord={requisitionStatusToLabel}
                        keyToBackgroundColorRecord={
                          requisitionStatusToBackgroundColor
                        }
                        keyToHoverBackgroundColorRecord={
                          requisitionStatusToHoverBackgroundColor
                        }
                      />
                    )}
                    {groupBy !== JobTrackerGroupBy.RequisitionOwner && (
                      <TooltipBadgesCell
                        keyToCountRecord={job.countsByOwner}
                        keyToLabelRecord={userNameMap}
                      />
                    )}
                    <TableCell>
                      {latestJobUpdate?.description ? (
                        <TableTooltipParagraph
                          text={latestJobUpdate.description}
                        />
                      ) : (
                        <EmptyDataDash />
                      )}
                    </TableCell>
                    <TableCell>
                      {latestJobUpdate?.bottleneck ? (
                        <JobBottleneckBadge
                          bottleneck={latestJobUpdate.bottleneck}
                        />
                      ) : (
                        <EmptyDataDash />
                      )}
                    </TableCell>
                    <TableCell>
                      {latestJobUpdate?.health ? (
                        <JobHealthBadge health={latestJobUpdate.health} />
                      ) : (
                        <EmptyDataDash />
                      )}
                    </TableCell>
                    <TableCell>
                      {latestJobUpdate ? (
                        <TimeAgoInTableCell
                          timestamp={latestJobUpdate.createdAt}
                        />
                      ) : (
                        <EmptyDataDash />
                      )}
                    </TableCell>
                    <TableCell>
                      $
                      {job.outstandingRevenue.toLocaleString(undefined, {
                        maximumFractionDigits: 2,
                      })}
                    </TableCell>
                    <TableCell>
                      <Button
                        onClick={(e) => {
                          e.stopPropagation();
                          window.open(
                            `/companies/${job.company.id}/jobs/${job.jobId}?t=pipeline`,
                            "_blank",
                          );
                        }}
                        variant="ghost"
                      >
                        <ArrowUpRightIcon />
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })
            ) : (
              <TableRow>
                <TableCell className="p-0" colSpan={25}>
                  <EmptyState
                    className="bg-slate-50 py-14"
                    description="No jobs were found. Try clearing some filters to see jobs!"
                    Icon={FileIcon}
                    primaryButtonName="Clear All Filters"
                    PrimaryButtonIcon={FilterXIcon}
                    handlePrimaryClick={() => {
                      handleClearAllFilters({
                        ...jobQueryParams,
                        bottlenecks: [],
                        difficulties: [],
                        healths: [],
                        jobStatuses: [],
                        ownerIds: [],
                        requisitionStatuses: [],
                        urgencies: [],
                      });
                    }}
                    title="No jobs found"
                  />
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </table>
      </div>
    );
  };

  return render();
};

const TooltipBadgesCell = ({
  keyToCountRecord,
  keyToLabelRecord,
  keyToBackgroundColorRecord,
  keyToHoverBackgroundColorRecord,
}: {
  keyToCountRecord: Record<string | number, number>;
  keyToLabelRecord: Record<string | number, string>;
  keyToBackgroundColorRecord?: Record<string, string>;
  keyToHoverBackgroundColorRecord?: Record<string, string>;
}) => {
  const hoverTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const objectsWithCounts = Object.entries(keyToCountRecord).filter(
    ([, count]) => count > 0,
  );
  const objectsToShow = objectsWithCounts.slice(0, 2);
  const remaining = objectsWithCounts.length - objectsToShow.length;
  const render = () => {
    return (
      <TableCell>
        <TooltipProvider delayDuration={300}>
          <Tooltip>
            <TooltipTrigger asChild>
              <div
                onMouseEnter={() => {
                  hoverTimerRef.current = setTimeout(() => {
                    mixpanelUtils.trackHover(
                      "+X more badge tooltip",
                      MIXPANEL_SCREENS.JOB_TRACKER,
                    );
                  }, 300);
                }}
                onMouseLeave={() => {
                  if (hoverTimerRef.current) {
                    clearTimeout(hoverTimerRef.current);
                    hoverTimerRef.current = null;
                  }
                }}
              >
                <Column className="gap-1">
                  {objectsToShow.map(([key, count], idx) => (
                    <Row key={key} className="items-center gap-1">
                      <Badge
                        className={cn(
                          keyToBackgroundColorRecord?.[key],
                          keyToHoverBackgroundColorRecord?.[key],
                        )}
                        variant="secondary"
                      >
                        {count} - {keyToLabelRecord[key]}
                      </Badge>
                      {idx === 1 && remaining > 0 ? (
                        <Badge variant="outline">+{remaining}</Badge>
                      ) : null}
                    </Row>
                  ))}
                </Column>
              </div>
            </TooltipTrigger>
            {remaining > 0 ? (
              <TooltipContent>
                <Column className="items-start gap-2">
                  {objectsWithCounts.map(([key, count]) => (
                    <Badge
                      key={key}
                      className={cn(
                        keyToBackgroundColorRecord?.[key],
                        keyToHoverBackgroundColorRecord?.[key],
                      )}
                      variant="secondary"
                    >
                      {count} - {keyToLabelRecord[key]}
                    </Badge>
                  ))}
                </Column>
              </TooltipContent>
            ) : null}
          </Tooltip>
        </TooltipProvider>
      </TableCell>
    );
  };

  return render();
};
